/**
 * Affiche un lien d'invitation — toujours visible et sélectionnable — avec
 * « Copier » et, si l'appareil le permet, « Partager ».
 *
 * Le lien n'est JAMAIS seulement « copié en douce » : la copie automatique après
 * un appel réseau échoue régulièrement (geste utilisateur perdu, cf.
 * `@/lib/clipboard`), donc l'utilisateur a toujours sous les yeux de quoi le
 * copier — bouton (geste direct, fiable) ou sélection manuelle.
 */

import { useRef, useState } from 'react';
import { Copy, Share2 } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { copyText } from '@/lib/clipboard';

export function InviteLinkField({
  url,
  shareTitle = 'Rejoins Jeraisonne',
}: {
  url: string;
  shareTitle?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  async function handleCopy() {
    const ok = await copyText(url);
    if (ok) {
      setState('copied');
      setTimeout(() => setState('idle'), 2000);
      return;
    }
    // Dernier recours : le lien est sélectionné, l'utilisateur le copie lui-même.
    inputRef.current?.select();
    setState('failed');
  }

  async function handleShare() {
    try {
      await navigator.share({ url, title: shareTitle });
    } catch {
      // partage annulé ou non disponible — le lien reste copiable
    }
  }

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        readOnly
        value={url}
        aria-label="Lien d'invitation"
        onFocus={(e) => e.currentTarget.select()}
        onClick={(e) => e.currentTarget.select()}
        className="border-ink-200 bg-ink-50 text-ink-700 focus:border-primary-500 h-11 w-full rounded-xl border px-3 font-mono text-xs focus:outline-none"
      />
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-11 flex-1"
          onClick={handleCopy}
        >
          <Copy className="h-3.5 w-3.5" />
          {state === 'copied' ? 'Copié !' : 'Copier le lien'}
        </Button>
        {canShare && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-11 flex-1"
            onClick={handleShare}
          >
            <Share2 className="h-3.5 w-3.5" />
            Partager
          </Button>
        )}
      </div>
      {state === 'failed' && (
        <p role="alert" className="text-ink-500 font-sans text-xs">
          Copie automatique impossible : le lien est sélectionné, copie-le (Ctrl/⌘ + C, ou appui
          long sur mobile).
        </p>
      )}
    </div>
  );
}
